import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { FirebaseService } from './firebaseService';
import { onAuthStateChanged, User } from 'firebase/auth';

interface FirebaseContextType {
  firebaseService: FirebaseService | null;
  isAuthenticated: boolean;
  currentUser: User | null;
  authLoading: boolean;
  firebaseInitialized: boolean;
  isAdmin: boolean;
}

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseService, setFirebaseService] = useState<FirebaseService | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(true); // Start as true during SSR
  const [firebaseInitialized, setFirebaseInitialized] = useState<boolean>(false);
  const [isClient, setIsClient] = useState<boolean>(false);

  // Check if we're on the client side
  useEffect(() => {
    setIsClient(true);
    setAuthLoading(false); // Set to false once we're on client
  }, []);

  // Initialize Firebase Service only on client side
  useEffect(() => {
    if (!isClient) return;

    const service = new FirebaseService({
      logger: console
    });
    setFirebaseService(service);
  }, [isClient]);

  const initializeUserData = useCallback(async (uid: string) => {
    if (!firebaseService || !isClient) return;

    try {
      // Initialize secrets if needed
      try {
        const initResponse = await fetch('/api/init-secrets', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (initResponse.ok) {
          const initResult = await initResponse.json();
          console.log('Secrets initialized:', initResult);
        }
      } catch (secretsError) {
        console.warn('Secrets initialization failed, continuing with env vars:', secretsError);
      }

      // Check admin status
      const adminStatus = await firebaseService.checkUserAdminStatus(uid);
      setIsAdmin(adminStatus);

      console.log('Firebase service ready for user:', uid);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Failed to initialize user data:', errorMessage);
    }
  }, [firebaseService, isClient]);


  // Firebase initialization check with retry and timeout and set up auth listener
  useEffect(() => {
    if (!firebaseService || !isClient) return;

    console.log('🚀 Starting Firebase initialization check...');
    let retryCount = 0;
    const maxRetries = 15;
    let unsubscribeAuth: (() => void) | null = null;

    const checkFirebaseInit = async () => {
      console.log(`🔄 Firebase init attempt ${retryCount + 1}/${maxRetries}`);

      try {
        const auth = await firebaseService.getFirebaseAuth();

        if (auth && typeof auth.onAuthStateChanged === 'function') {
          console.log('✅ Firebase Auth initialized successfully!');
          setFirebaseInitialized(true);

          // Moved inline instead of using useCallback
          setAuthLoading(true);
          unsubscribeAuth = onAuthStateChanged(
            auth,
            (user) => {
              console.log('🔄 Auth state changed:', user ? `User: ${user.uid}` : 'No user');
              setCurrentUser(user);
              setIsAuthenticated(!!user);
              setAuthLoading(false);

              if (user && firebaseService) {
                firebaseService.setDefaultAccountID(user.uid);
                initializeUserData(user.uid);
              } else {
                setIsAdmin(false);
              }
            },
            (error) => {
              console.error('❌ Auth state change error:', error);
              setAuthLoading(false);
            }
          );
        } else {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkFirebaseInit, 2000);
          } else {
            setFirebaseInitialized(true);
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('❌ Error during Firebase init check:', errorMessage);
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(checkFirebaseInit, 2000);
        } else {
          setFirebaseInitialized(true);
        }
      }
    };

    checkFirebaseInit();

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, [firebaseService, isClient, initializeUserData]);

  const value = {
    firebaseService,
    isAuthenticated,
    currentUser,
    authLoading,
    firebaseInitialized,
    isAdmin
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};